import React, { useEffect, useState } from 'react';
import { getConsent, setConsent, onConsentChange, initMetaTracking } from '../lib/metaTracking';
import { Link } from '../lib/router';

// Opens the settings dialog from anywhere (e.g. the footer link).
export const openTrackingSettings = () => window.dispatchEvent(new Event('nn-open-tracking-settings'));

export const ConsentBanner: React.FC = () => {
  const [consent, setState] = useState(getConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    initMetaTracking();
    const off = onConsentChange(() => setState(getConsent()));
    const open = () => setSettingsOpen(true);
    window.addEventListener('nn-open-tracking-settings', open);
    return () => { off(); window.removeEventListener('nn-open-tracking-settings', open); };
  }, []);

  const choose = (granted: boolean) => { setConsent(granted); setSettingsOpen(false); };

  if (settingsOpen) {
    const on = consent === 'granted';
    return (
      <div className="fixed inset-0 z-[13000] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/80" onClick={() => setSettingsOpen(false)} />
        <div role="dialog" aria-modal="true" aria-label="Tracking-Einstellungen" className="relative max-w-lg w-full bg-night-900 border border-white/10 rounded-2xl p-6 shadow-xl">
          <h3 className="text-xl font-bold text-white mb-2">Tracking-Einstellungen</h3>
          <p className="text-sm text-gray-400 mb-4">
            Mit deiner Einwilligung nutzen wir das Meta-Pixel und die Meta Conversions API (Facebook/Instagram, Meta Platforms Ireland Ltd.), um zu messen, welche Anzeigen zu Käufen führen, und dir passende Werbung zu zeigen. Dabei werden Seitenaufrufe, Kaufdetails sowie deine Profil- und Kontaktdaten (z. B. Name, E-Mail, Telefon, Wohnort, Geburtsdatum – gehasht) übertragen – bei Events und Käufen eines Veranstalters, der einen eigenen Meta-Pixel hinterlegt hat, auch an dessen Pixel. Du kannst deine Entscheidung jederzeit ändern. Mehr dazu in der <Link to="/datenschutz" className="underline text-purple-300">Datenschutzerklärung</Link>.
          </p>
          <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 p-4 mb-5">
            <div>
              <div className="font-semibold text-white">Marketing (Meta Pixel)</div>
              <div className="text-xs text-gray-400">{on ? 'Aktiv – du hast eingewilligt.' : 'Inaktiv – es werden keine Daten an Meta gesendet.'}</div>
            </div>
            <button
              role="switch" aria-checked={on}
              onClick={() => setConsent(!on)}
              className={`w-14 h-8 rounded-full p-1 transition-colors ${on ? 'bg-purple-600' : 'bg-gray-600'}`}>
              <span className={`block w-6 h-6 rounded-full bg-white transition-transform ${on ? 'translate-x-6' : ''}`} />
            </button>
          </div>
          <div className="flex justify-end">
            <button onClick={() => setSettingsOpen(false)} className="px-6 py-3 rounded-full text-white bg-gradient-to-r from-purple-600 to-indigo-500 font-semibold">Fertig</button>
          </div>
        </div>
      </div>
    );
  }

  if (consent !== null) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[13000] p-3 sm:p-4">
      <div role="dialog" aria-label="Einwilligung Marketing-Tracking" className="max-w-3xl mx-auto bg-night-900 border border-white/10 rounded-2xl p-5 shadow-2xl">
        <div className="font-bold text-white mb-1">Darf Night Navi Marketing-Tracking nutzen?</div>
        <p className="text-sm text-gray-400 mb-4">
          Wir möchten das Meta-Pixel und die Meta Conversions API (Facebook/Instagram) einsetzen, um Werbung zu messen und dir passende Anzeigen zu zeigen. Das schließt Veranstalter ein, die einen eigenen Pixel hinterlegt haben. Ohne deine Zustimmung wird nichts an Meta übertragen. Du kannst jederzeit über „Tracking-Einstellungen“ im Footer widerrufen. Details in der <Link to="/datenschutz" className="underline text-purple-300">Datenschutzerklärung</Link>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button onClick={() => choose(false)} className="px-6 py-3 rounded-full bg-white/10 text-white hover:bg-white/15 font-semibold">Ablehnen</button>
          <button onClick={() => choose(true)} className="px-6 py-3 rounded-full text-white bg-gradient-to-r from-purple-600 to-indigo-500 font-semibold">Zustimmen</button>
        </div>
      </div>
    </div>
  );
};
